import appServices from '@/services/appServices';
import { AppBankAccount } from '@polybank/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

interface Institution {
  id: string;
  name: string;
  logo: string;
}

/**
 * Hook to fetch institution logos for bank accounts.
 * Uses TanStack Query with aggressive caching since logos rarely change.
 */
export function useInstitutionLogos(bankAccounts: AppBankAccount[]) {
  // Extract unique institution IDs from bank accounts
  const institutionIds = useMemo(() => {
    const ids = new Set<string>();
    bankAccounts.forEach((account) => {
      if (account.provider_institution_id) {
        ids.add(account.provider_institution_id);
      }
    });
    return Array.from(ids);
  }, [bankAccounts]);

  // Fetch institutions for all countries we need
  // Since we don't have a single-institution endpoint, we'll fetch by country
  // and cache the results
  const institutionsQuery = useQuery({
    queryKey: ['institutions', 'all-countries'],
    queryFn: async () => {
      // Fetch institutions for common countries
      // This is a workaround since we don't have a single-institution API endpoint
      const countries = ['IT', 'GB', 'DE', 'FR', 'NL', 'SE', 'CA', 'AU'];

      const results = await Promise.allSettled(
        countries.map((country) =>
          appServices.bankConnections.getInstitutions(country)
        )
      );

      const allInstitutions: Institution[] = [];

      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value) {
          allInstitutions.push(...result.value);
        }
      });

      // Create a map for quick lookups
      const logoMap: Record<string, string> = {};
      allInstitutions.forEach((inst) => {
        if (inst.id && inst.logo) {
          logoMap[inst.id] = inst.logo;
        }
      });

      return logoMap;
    },
    staleTime: Infinity, // Logos never go stale
    gcTime: Infinity, // Keep in cache forever
    enabled: institutionIds.length > 0,
  });

  // Create a map of institution ID to logo URL
  const logoMap = useMemo(() => {
    return institutionsQuery.data || {};
  }, [institutionsQuery.data]);

  // Helper function to get logo for a specific account
  const getLogoForAccount = (account: AppBankAccount): string | undefined => {
    if (account.provider_institution_id) {
      return logoMap[account.provider_institution_id];
    }
    return undefined;
  };

  return {
    logoMap,
    getLogoForAccount,
    isLoading: institutionsQuery.isLoading,
    error: institutionsQuery.error,
  };
}
